import { format, isValid, parseISO } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import type { ICohortQueryModel } from "@performyard/models";
import {
	InputText,
	type PYColumnDef,
	Pagination,
	type SortChangedEvent,
	Spinner,
	Table,
	Text,
	useWizard,
} from "@performyard/py-components";
import type {
	PreviewSubjectsSortDirection,
	PreviewSubjectsSortName,
	ReviewCyclePreviewSubjectJson,
} from "@performyard/service-contracts";

import {
	useCreateReviewCyclePreviewSubjects,
	useReviewCyclePreviewSubjectsResults,
} from "../../../../../../api/hooks/defs/review-cycle";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import { addTimezoneOffset } from "../../../../../../utils/dateUtils";

import { QUALIFYING_SUBJECTS_STEP_ID } from "./constants";

const PAGE_LIMIT = 50;
const SORT_COL_IDS = ["name", "manager", "group", "hireDate"] as const;

type AutomationType = "oneTime" | "recurring";

type QualifyingSubjectsStepProps = {
	stepTitle: string;
	autoflowUserQuery: ICohortQueryModel | null;
};

const buildSubtext = (automationType: AutomationType | undefined, startDate: Date | undefined): string => {
	if (!startDate) return "";
	const formatted = format(startDate, "MMMM d yyyy 'at' h:mm a");
	if (automationType === "recurring") {
		return `**PerformYard will continuously monitor for new subjects to assign to this Review Cycle starting ${formatted} and every day at midnight.`;
	}
	return `**PerformYard will assign this Review Cycle to any qualifying subjects on ${formatted}.`;
};

export const QualifyingSubjectsStep = ({ stepTitle, autoflowUserQuery }: QualifyingSubjectsStepProps) => {
	const { setStepData, wizardData, setNextDisabled } = useWizard();

	const settings = wizardData["basic-info"] as { automationType?: AutomationType; startDate?: Date } | undefined;

	const { value: isGroupsEnabled } = useFeatureFlagQuery<boolean>("ff-is-groups-enabled", false);

	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
	const [sortName, setSortName] = useState<PreviewSubjectsSortName>("name");
	const [sortDirection, setSortDirection] = useState<PreviewSubjectsSortDirection>("asc");
	const [page, setPage] = useState(1);
	const [previewId, setPreviewId] = useState<string | null>(null);

	const createPreview = useCreateReviewCyclePreviewSubjects({
		onSuccess: (response) => setPreviewId(response.data.previewId),
	});

	useEffect(() => {
		const hasQueryConditions = !!autoflowUserQuery && autoflowUserQuery.and.length > 0;
		if (!hasQueryConditions) {
			setPreviewId(null);
			return;
		}
		setPreviewId(null);
		createPreview.mutate({ userQuery: autoflowUserQuery });
	}, [autoflowUserQuery]);

	const [prevFilters, setPrevFilters] = useState({
		search: debouncedSearchTerm,
		sortName,
		sortDirection,
		previewId,
	});
	if (
		prevFilters.search !== debouncedSearchTerm ||
		prevFilters.sortName !== sortName ||
		prevFilters.sortDirection !== sortDirection ||
		prevFilters.previewId !== previewId
	) {
		setPrevFilters({ search: debouncedSearchTerm, sortName, sortDirection, previewId });
		setPage(1);
	}

	const skip = (page - 1) * PAGE_LIMIT;
	const resultsParams = useMemo(
		() => ({
			search: debouncedSearchTerm || undefined,
			sortName,
			sortDirection,
			skip,
			limit: PAGE_LIMIT,
		}),
		[debouncedSearchTerm, sortName, sortDirection, skip]
	);

	const {
		data,
		isLoading: resultsLoading,
		isFetching: resultsFetching,
	} = useReviewCyclePreviewSubjectsResults({ previewId, params: resultsParams });

	const rows = data?.data ?? [];
	const total = data?.meta.pagination.total ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / PAGE_LIMIT));

	const isCreatingPreview = createPreview.isPending;
	const tableLoading = isCreatingPreview || resultsLoading || resultsFetching;

	useEffect(() => {
		setStepData(QUALIFYING_SUBJECTS_STEP_ID, { total });
	}, [total, setStepData]);

	useEffect(() => {
		setNextDisabled(isCreatingPreview || resultsLoading);
		return () => setNextDisabled(false);
	}, [isCreatingPreview, resultsLoading, setNextDisabled]);

	const columnDefs = useMemo<PYColumnDef<ReviewCyclePreviewSubjectJson>[]>(() => {
		const cols: PYColumnDef<ReviewCyclePreviewSubjectJson>[] = [
			{
				headerName: "Employee",
				colId: "name",
				type: "employee",
				sortable: true,
				flex: 1,
				valueGetter: (params) => ({
					_id: params.data?.userId ?? "",
					name: params.data?.name ?? "",
				}),
			},
		];
		if (isGroupsEnabled) {
			cols.push({
				headerName: "Group",
				colId: "group",
				type: "text",
				sortable: true,
				flex: 1,
				valueGetter: (params) => params.data?.groupName ?? "",
			});
		}
		cols.push(
			{
				headerName: "Hire Date",
				colId: "hireDate",
				type: "date",
				sortable: true,
				flex: 1,
				valueGetter: (params) => params.data?.hireDate ?? "",
				valueFormatter: (params) => {
					if (!params.value) return "";
					const date = typeof params.value === "string" ? parseISO(params.value) : new Date(params.value);
					if (!isValid(date)) return "";
					return format(addTimezoneOffset(date), "MMM dd, yyyy");
				},
			},
			{
				headerName: "Manager",
				colId: "manager",
				type: "employee",
				sortable: true,
				flex: 1,
				valueGetter: (params) => ({
					_id: params.data?.managerId ?? "",
					name: params.data?.managerName ?? "",
				}),
			}
		);
		return cols;
	}, [isGroupsEnabled]);

	const onSortChanged = (sort: SortChangedEvent) => {
		const col = sort.columns?.[0];
		const colId = col?.getColId();
		const direction = col?.getSort();
		if (!colId || !direction || !(SORT_COL_IDS as readonly string[]).includes(colId)) {
			setSortName("name");
			setSortDirection("asc");
			return;
		}
		setSortName(colId as PreviewSubjectsSortName);
		setSortDirection(direction as PreviewSubjectsSortDirection);
	};

	const subtext = buildSubtext(settings?.automationType, settings?.startDate);

	if (isCreatingPreview) {
		return (
			<div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
				<Text size="sub-heading" weight="semi-bold" color="neutral-9" dangerouslySetClassName="text-center">
					{stepTitle}
				</Text>
				<Text size="paragraph-2" color="neutral-9" dangerouslySetClassName="text-center">
					Qualifying Subjects
				</Text>
				<Spinner size={32} />
				<Text size="paragraph-3" color="neutral-6">
					Evaluating your automation rules...
				</Text>
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center">
			<Text size="sub-heading" weight="semi-bold" color="neutral-9" dangerouslySetClassName="text-center">
				{stepTitle}
			</Text>
			<Text size="paragraph-2" color="neutral-9" dangerouslySetClassName="text-center">
				Qualifying Subjects
			</Text>
			<div className="mt-8 flex w-full max-w-[1096px] flex-col px-4">
				<div className="flex flex-wrap items-end gap-2">
					<div className="w-72">
						<InputText
							value={searchTerm}
							onChange={setSearchTerm}
							leftIcon={faMagnifyingGlass}
							placeholder="Search..."
							clearable={searchTerm.length > 0}
							aria-label="Search qualifying subjects"
						/>
					</div>
					{subtext && (
						<Text size="extra-small" color="neutral-6" dangerouslySetClassName="ml-auto text-right">
							{subtext}
						</Text>
					)}
				</div>
				<div
					className="mb-2 mt-2 w-full"
					style={{ height: pageCount > 1 ? "calc(100dvh - 350px)" : "calc(100dvh - 318px)" }}
				>
					<Table<ReviewCyclePreviewSubjectJson>
						columnDefs={columnDefs}
						rowData={rows}
						loading={tableLoading}
						onSortChanged={onSortChanged}
						getRowId={(params) => params.data.userId}
						emptyStateText="No qualifying subjects found"
					/>
				</div>
				{pageCount > 1 && (
					<div className="mb-2 flex justify-end">
						<div className="flex w-full max-w-[280px] justify-end">
							<Pagination
								pageCount={pageCount}
								currentPage={page}
								onPageChange={setPage}
								dangerouslySetClassName="justify-end"
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
